/**
 * CreateConditionFromDocButton — a "Create condition" affordance for a document
 * view (file preview, extracts viewer, file-row actions). Clicking opens a small
 * "Quick condition" popover prefilled with a starter line about the document's
 * type; the user tweaks the text and creates it immediately (document
 * auto-attached). "More options" instead opens the full Add Condition sidebar.
 *
 * Props:
 *  - doc — the loan document object (needs `_id`; uses docType/docGroup to prefill)
 *  - variant — "button" (labeled) | "icon" (compact, for grid cells / toolbars)
 *  - onBeforeFullForm — called before opening the full sidebar (e.g. close the
 *    enclosing modal so the sidebar is visible). Quick-create does NOT call it —
 *    it stays on the file.
 */
import { useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import PlaylistAddCheckOutlined from "@mui/icons-material/PlaylistAddCheckOutlined";

import {
  useCreateConditionFromDocument,
  defaultConditionText,
} from "../../../../hooks/useCreateConditionFromDocument";

const CreateConditionFromDocButton = ({
  doc,
  variant = "button",
  label = "Create condition",
  size = "small",
  onBeforeFullForm,
}) => {
  const { openForDocument, quickCreate, creating, canCreate } =
    useCreateConditionFromDocument();
  const [anchorEl, setAnchorEl] = useState(null);
  const [text, setText] = useState("");

  const docId = doc?._id || doc?.id;
  if (!canCreate || !docId) return null;

  const open = Boolean(anchorEl);
  const docLabel = doc?.docType || doc?.docGroup || doc?.file_display_name;

  const handleOpen = (e) => {
    setText(defaultConditionText(doc));
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleQuick = () => {
    quickCreate(doc, {
      text: text?.trim() || defaultConditionText(doc),
      onSuccess: handleClose,
    });
  };

  const handleFull = () => {
    handleClose();
    onBeforeFullForm?.();
    openForDocument(doc, { text: text?.trim() || undefined });
  };

  return (
    <>
      {variant === "icon" ? (
        <Tooltip title="Create condition from this document">
          <IconButton size={size} color="primary" onClick={handleOpen}>
            <PlaylistAddCheckOutlined sx={{ fontSize: 19 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          size={size}
          variant="outlined"
          startIcon={<PlaylistAddCheckOutlined sx={{ fontSize: 18 }} />}
          onClick={handleOpen}
          sx={{ textTransform: "none", fontSize: 12 }}
        >
          {label}
        </Button>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 330, p: 1.75, borderRadius: 2 } } }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
          Quick condition
        </Typography>
        {docLabel && (
          <Typography sx={{ fontSize: 11.5, color: "#64748b", mt: 0.25 }}>
            Attaches: {docLabel}
          </Typography>
        )}
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          size="small"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. Get updated bank statement"
          sx={{ mt: 1.25, "& textarea": { fontSize: 13 } }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleQuick();
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 1.25,
          }}
        >
          <Button
            size="small"
            variant="text"
            onClick={handleFull}
            sx={{ textTransform: "none", fontSize: 12 }}
          >
            More options
          </Button>
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={handleQuick}
            disabled={creating || !text.trim()}
            sx={{ textTransform: "none", fontSize: 12 }}
          >
            {creating ? "Creating…" : "Create condition"}
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default CreateConditionFromDocButton;
